import { useEffect, useMemo, useCallback } from 'react';
import { DateTime } from 'luxon';
import { unpackToDateTime } from '@campfire/hot-date';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { RosterListItem, AvailableActivityTypeEnum, AvailableActivityStatusEnum } from '../main-section/roster/roster-types';

interface SidebarActivityData {
  activityId: string;
  name: string;
  startTime: string | null;
  endTime: string | null;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  minVolunteers: number;
  hasOpenRoster: boolean;
  isSuspended: boolean;
  isRecurring: boolean;
  closedActivityId: string | null;
  sessionCount: number;
  activeEnrolmentCount: number;
  occurrencesBetween: string[];
  historicalOccurrences: string[];
  publishedRosters: Record<string, { rosterId: string; dateRemoved: string | null; name: string | null }>;
  draftRosters: Record<string, { draftRosterId: string; publishedRosterId: string | null; publishedRosterRemoved: boolean }>;
  cancellations: Record<string, boolean>;
  pendingApprovals: Record<string, boolean>;
  rosterTemplates: Record<string, { rosterTemplateId: string; name: string | null; rosterNotes: string | null; isPendingApproval: boolean }>;
  activityReports: Record<string, { sessionReportCount: number }>;
  program: {
    programId: string;
    name: string;
    dateDeleted: string | null;
    dateSuspended: string | null;
  };
  activityTags: Array<{ activityTagId: string; name: string }>;
}

interface SidebarDataResponse {
  activities: SidebarActivityData[];
}

function getActivityStatus(activity: SidebarActivityData, date: string): AvailableActivityStatusEnum {
  const endTime = activity.endTime || '23:59:59';
  const isCompleted = activity.isActive && unpackToDateTime(`${date}T${endTime}`) < DateTime.local();

  if (activity.isSuspended) return 'suspended';
  if (activity.cancellations[date]) return 'cancelled';
  if (activity.closedActivityId !== null) return 'closed';
  if (activity.endDate && unpackToDateTime(activity.endDate) < DateTime.local()) return 'ended';
  if (isCompleted) return 'completed';

  return 'active';
}

export function useSidebarData(
  activityIds: string[] | undefined,
  startDate: string | undefined,
  endDate: string | undefined
) {
  const apiUrl = useApiUrl();

  const { run, response, isLoading, error } = useCampfireFetch<SidebarDataResponse>({
    defer: true,
  });

  const activityIdsKey = activityIds?.join(',') ?? '';

  useEffect(() => {
    if (!activityIds || activityIds.length === 0 || !startDate || !endDate) {
      return;
    }

    run({
      url: `${apiUrl}/vm/activity/roster/sidebar-data`,
      method: 'POST',
      data: {
        activityIds,
        startDate,
        endDate,
      },
    });
  }, [activityIdsKey, startDate, endDate, apiUrl]);

  const refetch = useCallback(() => {
    if (!activityIds || activityIds.length === 0 || !startDate || !endDate) {
      return;
    }

    run({
      url: `${apiUrl}/vm/activity/roster/sidebar-data`,
      method: 'POST',
      data: {
        activityIds,
        startDate,
        endDate,
      },
    });
  }, [activityIdsKey, startDate, endDate, apiUrl]);

  const rosters: RosterListItem[] | undefined = useMemo(() => {
    if (!response?.data?.data?.activities) {
      return undefined;
    }

    const { activities } = response.data.data;

    return activities
      .filter((a) => !a.program.dateSuspended && !a.program.dateDeleted)
      .flatMap((a) => {
        // Combine occurrences and historical occurrences, removing duplicates
        const allDates = Array.from(new Set([
          ...a.occurrencesBetween,
          ...a.historicalOccurrences,
        ]));

        const activityType: AvailableActivityTypeEnum = a.isRecurring ? 'regular' : 'flexible';

        return allDates.map((o) => ({
          activityId: a.activityId,
          activityName: a.name,
          startTime: a.startTime || '',
          endTime: a.endTime || '',
          activityDate: o,
          programId: a.program.programId,
          programName: a.program.name,
          activityType,
          activityStatus: getActivityStatus(a, o),
          volsNeeded: a.activeEnrolmentCount - a.minVolunteers - 1,
          hasOpenRoster: a.hasOpenRoster,
          activityTags: a.activityTags.map((tag) => ({ activityTagId: tag.activityTagId })),
          rosterName: a.publishedRosters[o]?.name || a.rosterTemplates[o]?.name || null,
        }));
      });
  }, [response]);

  // Build a data structure that mimics the GraphQL response for backward compatibility
  const data = useMemo(() => {
    if (!response?.data?.data?.activities) {
      return undefined;
    }

    return {
      vm: {
        activities: response.data.data.activities.map((a) => ({
          activityId: a.activityId,
          name: a.name,
          startTime: a.startTime,
          endTime: a.endTime,
          startDate: a.startDate,
          endDate: a.endDate,
          isActive: a.isActive,
          minVolunteers: a.minVolunteers,
          hasOpenRoster: a.hasOpenRoster,
          isSuspended: a.isSuspended,
          closedActivity: a.closedActivityId ? { closedActivityId: a.closedActivityId } : null,
          occurrencesBetween: a.occurrencesBetween,
          historicalOccurrences: a.historicalOccurrences,
          __typename: a.isRecurring ? 'VOLUNTEER_RecurringActivityType' : 'VOLUNTEER_NonRecurringActivityType',
          program: a.program,
          activityTags: a.activityTags,
          activityEnrolments: Array(a.activeEnrolmentCount).fill({ dateRemoved: null }),
          publishedRosters: Object.entries(a.publishedRosters).map(([date, roster]) => ({
            activityDate: date,
            dateRemoved: roster.dateRemoved,
          })),
          draftRosters: Object.entries(a.draftRosters).map(([date, draft]) => ({
            draftRosterId: draft.draftRosterId,
            activityDate: date,
            publishedRoster: draft.publishedRosterId ? {
              rosterId: draft.publishedRosterId,
              dateRemoved: draft.publishedRosterRemoved ? new Date().toISOString() : null,
            } : null,
          })),
          cancelledActivities: Object.keys(a.cancellations).map((date) => ({
            cancelledActivityId: `cancelled-${a.activityId}-${date}`,
            activityDate: date,
          })),
          rosterTemplates: Object.entries(a.rosterTemplates).map(([date, template]) => ({
            rosterTemplateId: template.rosterTemplateId,
            name: template.name,
            rosterDate: date,
            rosterNotes: template.rosterNotes,
            isPendingApproval: template.isPendingApproval,
          })),
          activityReports: Object.entries(a.activityReports).map(([date, report]) => ({
            activityDate: date,
            sessionReports: Array(report.sessionReportCount).fill({ sessionReportId: 'placeholder' }),
          })),
          sessions: Array(a.sessionCount).fill({ sessionId: 'placeholder' }),
        })),
      },
    };
  }, [response]);

  return {
    data,
    rosters,
    loading: isLoading,
    error,
    refetch,
  };
}
